import { TabsNavigation } from 'components/molecules';
import FormSubmitButtons from 'components/molecules/buttons/formSubmitButtons/FormSubmitButtons';
import Tabs from 'components/molecules/tabs';
import TopBar from 'components/organisms/top-bar/TopBar';
import { TopbarTabs } from 'components/templates';
import { Form, Formik, FormikConfig } from 'formik';
import { FormObserver } from 'hooks/useFormObserver';
import i18next from 'i18next';
import { useStoreState } from 'providers/StoreStateContext';
import {
  ITrackArtistMutationForm,
  ITrackMutationForm,
  TRACK_CENSORIOUS,
} from 'services/tracks/types';
import { ARTIST_ROLE, GENRE, STATUS } from 'services/types';
import { II18nextPrefix } from 'types/components/components.interface';
import {
  artistsValidation,
  conditionalField,
  credensialsValidation,
  getTabError,
  requiredEnumSelect,
  titlesValidation,
} from 'utils/global/validation.utils';
import { array, boolean, object, Schema, string } from 'yup';
import { IFormikProps, IFormProps } from '../types';
import TrackData from './tabs/TrackData';
import TrackFile from './tabs/TrackFile';
import TrackProduct from './tabs/TrackProducts';

export enum TrackTabs {
  file,
  data,
  artists,
  // lyrics,
  // titles,
  external,
  contracts,
}

export const trackInitialValues: ITrackMutationForm = {
  censorious: TRACK_CENSORIOUS.unknown,
  genre: { id: -1, name: '' },

  status: STATUS.draft,
  isInstrumental: true,
  mainTitle: {
    language: -1,
    title: '',
    version: '',
  },
  cline: { name: '', year: '' },
  pline: { name: '', year: '' },
  text: '',
  isrc: '',
  assignIsrc: false,
  otherTitles: [],
  artists: [],
  contracts: [],
  externalIds: [],
  statistics: { endSettlementPeriodId: '', startSettlementPeriodId: '' },
  isCreatedWithAi: false,
  id: '',
  version: 0,
};

const lyricistConditionalValidator = (baseSchema: Schema) =>
  baseSchema
    .when('isInstrumental', {
      is: (instrumental?: boolean) => !instrumental,
      then: (schema) =>
        schema.test(
          'has-lyricist',
          () => i18next.t('formik-error.track-should-have-lyricist'),
          (artist: ITrackArtistMutationForm[]) =>
            artist && artist.some((item) => item.role === ARTIST_ROLE.lyricist),
        ),
      otherwise: (schema) => schema,
    })
    .when('isInstrumental', {
      is: (instrumental?: boolean) => instrumental,
      then: (schema) =>
        schema.test(
          'has-not-lyricist',
          () => i18next.t('formik-error.track-should-not-have-lyricist'),
          (artists: ITrackArtistMutationForm[]) =>
            !!!artists.find((item) => item.role === ARTIST_ROLE.lyricist),
        ),
      otherwise: (schema) => schema,
    });

export const trackValidation = object().shape({
  status: requiredEnumSelect(STATUS),
  mainTitle: titlesValidation,

  censorious: conditionalField(requiredEnumSelect(TRACK_CENSORIOUS)),
  genre: conditionalField(object().shape({ id: requiredEnumSelect(GENRE) })),
  // source: conditionalField(requiredEnumSelect(TRACK_SOURCE)),
  isrc: string().when('assignIsrc', {
    is: (assignIsrc: boolean) => assignIsrc === false,
    then: (schema) => schema.required(() => i18next.t('formik-error.required')),
    otherwise: (schema) => schema.nullable().optional(),
  }),
  pline: credensialsValidation('status'),
  cline: credensialsValidation('status'),
  isInstrumental: boolean(),
  isCreatedWithAi: boolean(),
  assignIsrc: boolean(),
  text: string().nullable(),
  otherTitles: array().of(titlesValidation),
  artists: conditionalField(
    lyricistConditionalValidator(
      array()
        .of(artistsValidation)
        .min(1, () => i18next.t('formik-error.at-least-one-artist-required'))
        .test(
          'has-main-artist',
          () =>
            i18next.t('formik-error.what-should-have-main-artist', {
              what: i18next.t('track.what'),
            }),
          (artist) => artist && artist.some((item) => item.role === ARTIST_ROLE.mainArtist),
        )
        .test(
          'has-composer',
          () =>
            i18next.t('formik-error.what-should-have-composer', {
              what: i18next.t('track.what'),
            }),
          (artist) => artist && artist.some((item) => item.role === ARTIST_ROLE.composer),
        ),
    ),
    'status',
    [-1, 1],
    array().of(artistsValidation),
  ),
  file: conditionalField(
    string().when('wavLink', {
      is: (link?: string) => link === undefined,
      then: (schema) => schema.required(() => i18next.t('formik-error.required')),
      otherwise: (schema) => schema.optional().nullable(),
    }),
  ),
  wavLink: conditionalField(
    string().when('file', {
      is: (file?: File) => file === undefined,
      then: (schema) => schema.required(() => i18next.t('formik-error.required')),
      otherwise: (schema) => schema.optional().nullable(),
    }),
  ),
});

export interface ITrackForm extends FormikConfig<ITrackMutationForm>, IFormProps, II18nextPrefix {}

const TrackForm = ({
  i18nextPrefix,
  topbarContent,
  tittle,
  status,
  initialValues = trackInitialValues,
  disableTabs,
  backPath,
  customBackItem,
  ...props
}: ITrackForm) => {
  const { setForm } = useStoreState();
  return (
    <Formik
      {...props}
      validationSchema={trackValidation}
      validateOnChange={true}
      validateOnBlur={true}
      initialStatus={status}
      enableReinitialize
      initialValues={initialValues}
    >
      {({ values, setFieldValue, status, errors }: IFormikProps<ITrackMutationForm>) => (
        <Form className="w-full h-full">
          <FormObserver<ITrackMutationForm>
            onChange={(newValues) => {
              setForm('trackForm', newValues);
            }}
          />
          <TopbarTabs>
            <TopBar
              customBackItem={customBackItem}
              title={tittle}
              path={backPath ?? 'track'}
              topbarContent={
                <div className="flex  justify-end">
                  <div className="flex items-center gap-4 justify-end ">
                    {/* {!!initialValues.mainTitle.title.length && status?.displayMode && (
                      <LabeledStatus status={initialValues?.status} noname />
                    )} */}
                    {!status?.displayMode && <FormSubmitButtons statusName="status" />}
                    {topbarContent}
                  </div>
                </div>
              }
            >
              <TabsNavigation
                displayMode={status?.displayMode}
                tabs={[
                  {
                    value: TrackTabs.data,
                    name: 'data',
                    error: getTabError([
                      errors.mainTitle?.title,
                      errors.mainTitle?.version,
                      errors.isCreatedWithAi,
                      errors.isrc,
                      errors.censorious,
                      errors.genre?.id,
                      errors.pline,
                      errors.cline,
                      errors.isInstrumental,
                      errors.assignIsrc,
                    ]),
                  },
                  {
                    value: TrackTabs.file,
                    name: 'file',
                    error: getTabError([errors.file, errors.wavLink]),
                  },
                  // ...(!values.isInstrumental
                  //   ? [
                  //       {
                  //         value: TrackTabs.lyrics,
                  //         name: 'lyrics',
                  //         error: errors.text,
                  //       },
                  //     ]
                  //   : []),
                  {
                    value: TrackTabs.artists,
                    name: 'artists',
                    error: errors.artists,
                  },

                  // {
                  //   value: TrackTabs.titles,
                  //   name: 'titles',
                  //   error: errors.otherTitles,
                  // },
                  {
                    value: TrackTabs.external,
                    name: 'external',
                    error: errors.externalIds,
                    hideInEditMode: true,
                  },
                  {
                    value: TrackTabs.contracts,
                    name: 'contracts',
                    error: errors.contracts,
                    hideInEditMode: true,
                  },
                  // {
                  //   value: TrackTabs.products,
                  //   name: 'products',
                  // },
                ]}
                disabled={[...(disableTabs ?? [])]}
              />
            </TopBar>
          </TopbarTabs>
        </Form>
      )}
    </Formik>
  );
};

TrackForm.getRouterTabs = (disabledChildren?: string[]) =>
  [
    { path: 'data', element: <TrackData /> },
    // { path: 'lyrics', element: <TrackText /> },
    { path: 'file', element: <TrackFile /> },
    { path: 'contracts', element: <Tabs.ProductContracts /> },
    { path: 'artists', element: <Tabs.ArtistsRoleStep parent="TRACK" /> },
    // { path: 'titles', element: <Tabs.Titles parent="TRACK" /> },
    { path: 'external', element: <Tabs.ExternalId parent="TRACK" /> },
    { path: 'products', element: <TrackProduct /> },
  ].filter((children) => !disabledChildren?.includes(children.path));

export default TrackForm;
